import { useEffect, useState, useContext, useMemo } from 'react';
import { UserContext } from './UserContext';
import { isAdminType, canManageUser, canonicalRole } from './utils/roleUtils.js';
import Sidebar from './Sidebar.js';
import { Card, Image, Row, Col, Container, Form, Button, Modal, Table, Pagination } from 'react-bootstrap';
import Breadcrumb from './Breadcrumb.js';
import { Link } from 'react-router-dom';
import { useNavigate, useParams } from 'react-router-dom';
import UserAddGeneral from './UserAddGeneral.js';
import api from './api/axiosInstance.js';
import NavTabs from './NavTabs.js';
import icSucess from './assets/img/ic-celebration-circle.svg';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import { TicketCountContext } from './TicketCountContext';

const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const FIELD_LABELS = {
    firstName: 'First Name',
    familyName: 'Last Name',
    gender: 'Gender',
    profileImage: 'Profile Image',
    email: 'Email',
    birthDate: 'Birth Date',
    phone: 'Phone',
    company: 'Company',
    companyRole: 'Company Role',
    highestDegree: 'Highest Degree',
    currentDegree: 'Current Degree',
    currentEducationalInstitution: 'Institution',
    academicsCountry: 'Academics Country',
    academicsCity: 'Academics City',
    studyDirection: 'Study Direction',
    countriesWorkPreferences: 'Country Preferences',
    citiesWorkPreferences: 'City Preferences',
    languagesWorkPreferences: 'Language Preferences',
    industryJobPreferences: 'Industry Preferences',
    employmentJobPreferences: 'Employment Type',
    fteJobPreferences: 'FTE Preference',
    jobTypeJobPreferences: 'Job Type',
    companyGlobalSize: 'Company Global Size',
    companyLocalSize: 'Company Local Size',
    companyRating: 'Company Rating',
    fortune: 'Fortune',
    futureDegree: 'Future Degree',
    plannedGraduationYear: 'Graduation Year',
    citizenships: 'Citizenships',
    nationality: 'Nationality',
    country: 'Country',
    city: 'City',
    zipCity: 'Zip Code',
};

const UserEditHistory = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { user: actor } = useContext(UserContext);
    const actorRole = canonicalRole(actor?.role);
    const actorId = String(actor?._id || '');
    const { ticketCount } = useContext(TicketCountContext);

    const [errors, setErrors] = useState({});
    const [profileImage, setProfileImage] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [showSavedModal, setShowSavedModal] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [errorText, setErrorText] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);
    const [isLoadingUser, setIsLoadingUser] = useState(true);
    const [savedModalTitle] = useState('Update successful');
    const [savedModalMessage, setSavedModalMessage] = useState('');

    const [targetUserRole, setTargetUserRole] = useState('');

    const [generalData, setGeneralData] = useState({
        profileImage: '',
        firstName: '',
        familyName: '',
        gender: '',
        company: '',
        companyRole: '',
        activityStatus: false,
    });

    const [history, setHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const userNavItems = [
        { to: '/user-edit/:id', eventKey: 'userInfo', label: 'About' },
        { to: '/user-edit-academics/:id', eventKey: 'academicsInfo', label: 'Academics' },
        { to: '/user-edit-work-preferences/:id', eventKey: 'work', label: 'Work Preferences' },
        { to: '/user-edit-access/:id', eventKey: 'access', label: 'Access & Permissions' },
        { to: '/user-edit-history/:id', eventKey: 'history', label: 'History of changes' },
    ];

    const normalizeId = (x) => {
        if (!x) return '';
        if (typeof x === 'string') return x;
        if (typeof x === 'object') {
            if (x._id) return normalizeId(x._id);
            if (x.$oid) return String(x.$oid);
            if (x.id) return normalizeId(x.id);
            if (typeof x.toHexString === 'function') return x.toHexString();
            if (typeof x.toString === 'function' && x.toString !== Object.prototype.toString) {
                const s = x.toString();
                if (/^[a-fA-F0-9]{24}$/.test(s)) return s;
            }
        }
        return '';
    };

    useEffect(() => {
        api.get('/api/companies').then(res => setCompanies(res.data)).catch(() => {});
    }, []);

    useEffect(() => {
        const fetchUserData = async () => {
            setIsLoadingUser(true);
            setShowErrorMessage(false);
            setErrorText('');

            try {
                const res = await api.get(`/api/users/${id}`);
                const currentUser = res.data;

                if (!currentUser) {
                    setErrorText('User not found.');
                    setShowErrorMessage(true);
                    return;
                }

                setTargetUserRole(canonicalRole(currentUser.role || ''));

                setGeneralData({
                    profileImage: currentUser.profileImage || '',
                    firstName: currentUser.firstName || '',
                    familyName: currentUser.familyName || '',
                    gender: currentUser.gender || '',
                    profileImagePreview: currentUser.profileImage || '',
                    company: normalizeId(currentUser.company),
                    companyRole: currentUser.companyRole || '',
                    activityStatus: currentUser.activityStatus || false,
                });
            } catch (error) {
                const status = error?.response?.status;
                if (status === 403) {
                    setErrorText('Access denied: you do not have permission to view this user.');
                    setShowErrorMessage(true);
                    return;
                }
                console.error('Error fetching user data:', error);
                setErrorText('An error occurred while fetching user details.');
                setShowErrorMessage(true);
            } finally {
                setIsLoadingUser(false);
            }
        };
        fetchUserData();
    }, [id]);

    useEffect(() => {
        const fetchHistory = async () => {
            setIsLoadingHistory(true);
            try {
                const res = await api.get(`/api/users/${id}/history`);
                setHistory(res.data || []);
            } catch (err) {
                console.error('Error fetching history:', err);
            } finally {
                setIsLoadingHistory(false);
            }
        };
        fetchHistory();
    }, [id]);

    const isSelfEdit = actorId && actorId === String(id);

    const hideEditSameOrHigher =
        !isSelfEdit &&
        isAdminType(actorRole) &&
        isAdminType(targetUserRole) &&
        !canManageUser(actorRole, targetUserRole);

    const validateGeneralFields = () => {
        const newErrors = {};
        if (!generalData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!generalData.familyName.trim()) newErrors.familyName = 'Last name is required';
        if (!generalData.gender) newErrors.gender = 'Gender is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleGeneralSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!validateGeneralFields()) return false;

        try {
            const data = new FormData();
            data.append('firstName', generalData.firstName || '');
            data.append('familyName', generalData.familyName || '');
            data.append('gender', generalData.gender || '');
            if (profileImage) data.append('profileImage', profileImage);

            await api.put(`/api/users/${id}`, data);

            setSavedModalMessage('User General information updated successfully.');
            setShowSavedModal(true);
            return true;
        } catch (error) {
            console.error('Error updating general information:', error);
            setShowErrorMessage(true);
            return false;
        }
    };

    const handleGeneralChange = (e) => {
        const { name, value, files } = e.target;

        if (name === 'profileImage' && files && files.length > 0) {
            const file = files[0];
            setProfileImage(file);
            setGeneralData((prev) => ({ ...prev, profileImagePreview: URL.createObjectURL(file) }));
            return;
        }

        setGeneralData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleGeneralReset = (resetData) => {
        setGeneralData(resetData);
        setErrors({});
    };

    const isActive = generalData.activityStatus;

    const handleDeactivate = async () => {
        try {
            const endpoint = isActive ? `/api/users/${id}/deactivate` : `/api/users/${id}/activate`;
            await api.put(endpoint);
            setShowDeactivateModal(false);
            setGeneralData((prev) => ({ ...prev, activityStatus: !isActive }));
        } catch (err) {
            console.error('Error toggling account status:', err);
        }
    };

    const handleDelete = async () => {
        try {
            await api.delete(`/api/users/${id}`);
            setShowDeleteModal(false);
            navigate('/onboarding-user-list');
        } catch (err) {
            console.error('Error deleting account:', err);
        }
    };

    const flatRows = useMemo(() => {
        const rows = [];
        history.forEach((entry) => {
            const changedBy = entry.changedBy;
            const name = changedBy
                ? `${changedBy.firstName || ''} ${changedBy.familyName || ''}`.trim() || '—'
                : '—';
            const changes = entry.changes?.length ? entry.changes : [{ field: '', oldValue: '', newValue: '' }];
            changes.forEach((c) => {
                rows.push({ entryId: entry._id, date: entry.createdAt, section: entry.section, field: c.field, oldValue: c.oldValue, newValue: c.newValue, name });
            });
        });
        return rows;
    }, [history]);

    const totalPages = Math.ceil(flatRows.length / rowsPerPage);
    const currentRows = flatRows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    const getPaginationItems = () => {
        if (totalPages <= 1) return [1];
        const range = (s, e) => { const o = []; for (let i = s; i <= e; i++) o.push(i); return o; };
        const boundary = 1, sibling = 2;
        const startPages = range(1, Math.min(boundary, totalPages));
        const endPages = range(Math.max(totalPages - boundary + 1, boundary + 1), totalPages);
        const siblingsStart = Math.max(Math.min(currentPage - sibling, totalPages - boundary - sibling * 2 - 1), boundary + 2);
        const siblingsEnd = Math.min(Math.max(currentPage + sibling, boundary + sibling * 2 + 2), totalPages - boundary - 1);
        const items = [
            ...startPages,
            ...(siblingsStart > boundary + 2 ? ['ellipsis'] : boundary + 1 < totalPages - boundary ? [boundary + 1] : []),
            ...range(siblingsStart, siblingsEnd),
            ...(siblingsEnd < totalPages - boundary - 1 ? ['ellipsis'] : totalPages - boundary > boundary ? [totalPages - boundary] : []),
            ...endPages,
        ];
        return items.filter((v, i) => items.indexOf(v) === i);
    };

    if (isLoadingUser) {
        return (
            <>
                <Sidebar ticketCount={ticketCount} />
                <main className="main-content flex-grow-1">
                    <Container><p className="mt-4">Loading user...</p></Container>
                </main>
            </>
        );
    }

    if (showErrorMessage && errorText) {
        return (
            <>
                <Sidebar ticketCount={ticketCount} />
                <main className="main-content flex-grow-1">
                    <Container><p className="mt-4">{errorText}</p></Container>
                </main>
            </>
        );
    }

    return (
        <>
            <Sidebar ticketCount={ticketCount} />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Card>
                                <Card.Body className="pt-0">
                                    <Row>
                                        <Col xs={12}>
                                            <>
                                                <Row>
                                                    <Col>
                                                        <Form onSubmit={handleGeneralSubmit}>
                                                            <UserAddGeneral
                                                                formData={{ ...generalData }}
                                                                profileImage={generalData.profileImagePreview}
                                                                handleChange={handleGeneralChange}
                                                                handleGeneralSubmit={handleGeneralSubmit}
                                                                errors={errors}
                                                                isEditPage={true}
                                                                handleReset={handleGeneralReset}
                                                                companies={companies}
                                                                hideEdit={hideEditSameOrHigher}
                                                            />
                                                        </Form>
                                                    </Col>
                                                </Row>
                                                <Row className="mb-5">
                                                    <Col>
                                                        <NavTabs items={userNavItems} />
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col>
                                                        <h5 className="mb-4">History of changes</h5>
                                                        {isLoadingHistory ? (
                                                            <p>Loading history...</p>
                                                        ) : flatRows.length === 0 ? (
                                                            <p className="text-muted">No changes recorded yet.</p>
                                                        ) : (
                                                            <>
                                                                <Table hover responsive>
                                                                    <thead>
                                                                        <tr>
                                                                            <th>#</th>
                                                                            <th>Date & Time</th>
                                                                            <th>Section</th>
                                                                            <th>Field</th>
                                                                            <th>Old Value</th>
                                                                            <th>New Value</th>
                                                                            <th>Changed by</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {currentRows.map((row, idx) => (
                                                                            <tr key={`${row.entryId}-${idx}`}>
                                                                                <td>{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                                                                                <td>{formatDate(row.date)}</td>
                                                                                <td>{row.section}</td>
                                                                                <td>{FIELD_LABELS[row.field] || row.field || '—'}</td>
                                                                                <td>{row.oldValue || '—'}</td>
                                                                                <td>{row.newValue || '—'}</td>
                                                                                <td>{row.name}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </Table>
                                                                <Row className="pagination-wrapper mt-4">
                                                                    <Col className="d-flex align-items-center">
                                                                        <span>Total {flatRows.length} {flatRows.length === 1 ? 'change' : 'changes'}</span>
                                                                        <Pagination>
                                                                            <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}>
                                                                                <BsChevronLeft />
                                                                            </Pagination.Prev>
                                                                            {getPaginationItems().map((item, i) =>
                                                                                item === 'ellipsis'
                                                                                    ? <Pagination.Ellipsis key={`e-${i}`} disabled />
                                                                                    : <Pagination.Item key={item} active={item === currentPage} onClick={() => setCurrentPage(item)}>{item}</Pagination.Item>
                                                                            )}
                                                                            <Pagination.Next onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages || totalPages === 0}>
                                                                                <BsChevronRight />
                                                                            </Pagination.Next>
                                                                        </Pagination>
                                                                    </Col>
                                                                    <Col className="pagination-controls">
                                                                        <div className="pagination-controls-per-page">
                                                                            <Form.Select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                                                                <option value="10">10 / page</option>
                                                                                <option value="20">20 / page</option>
                                                                                <option value="40">40 / page</option>
                                                                            </Form.Select>
                                                                        </div>
                                                                        <label className="pagination-controls-goto-label" htmlFor="history-goto-page">Go to</label>
                                                                        <Form.Control
                                                                            id="history-goto-page"
                                                                            className="pagination-controls-goto-input"
                                                                            type="number"
                                                                            value={currentPage}
                                                                            onChange={(e) => { const p = Number(e.target.value); if (p >= 1 && p <= totalPages) setCurrentPage(p); }}
                                                                            onBlur={() => { if (currentPage < 1) setCurrentPage(1); if (currentPage > totalPages) setCurrentPage(totalPages); }}
                                                                        />
                                                                    </Col>
                                                                </Row>
                                                            </>
                                                        )}
                                                    </Col>
                                                </Row>
                                            </>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <Row className="buttons-row">
                        <Col>
                            <Link to="/user-add" className="btn btn-outline-primary">
                                Add new user
                            </Link>
                            <Button variant="outline-secondary" onClick={() => setShowDeactivateModal(true)}>{isActive ? 'Deactivate account' : 'Activate account'}</Button>
                            <Button variant="outline-danger" onClick={() => setShowDeleteModal(true)}>Delete account</Button>
                        </Col>
                    </Row>
                </Container>
            </main>

            <Modal show={showDeactivateModal} onHide={() => setShowDeactivateModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{isActive ? 'Deactivate account' : 'Activate account'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{isActive
                        ? 'Are you sure you want to deactivate this account?'
                        : 'Are you sure you want to reactivate this account?'
                    }</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary me-3" onClick={() => setShowDeactivateModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleDeactivate}>{isActive ? 'Deactivate' : 'Activate'}</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Delete account</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to permanently delete this account? This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary me-3" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
                    <Button variant="danger" onClick={handleDelete}>Delete account</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showSavedModal} onHide={() => setShowSavedModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Image src={icSucess} alt="" className="d-block m-auto mb-4" />
                    <h2 className="text-center mb-3">{savedModalTitle}</h2>
                    <p className="text-center">{savedModalMessage}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowSavedModal(false)}>OK</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default UserEditHistory;
